import { isFullDayLeave } from "@/requests/leaves/lib/isFullDayLeave"
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Loader2, Files, CheckCircle2, XCircle, AlertCircle,
    ArrowLeft, CalendarDays, User, ClipboardList, Watch, Timer, File
} from 'lucide-react';
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Link } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import { formatDuration } from '@/requests/leaves/lib/calculateLeaveDuration';
import { fetchUsersWithProfiles } from '@/lib/manager realtime/userCache';
import { getLeaveById, type LeaveRequest } from '@/lib/collections/requestsLeave/rrhh/getAllLeaveRequests';

interface Props { leaveId: string; }

const STATUS = {
    pending: { bg: '#FAEEDA', border: '#EF9F27', dot: '#BA7517', text: '#633806', label: 'Pendiente', Icon: AlertCircle },
    approved: { bg: '#E1F5EE', border: '#1D9E75', dot: '#0F6E56', text: '#04342C', label: 'Aprobado', Icon: CheckCircle2 },
    rejected: { bg: '#FCEBEB', border: '#E24B4A', dot: '#A32D2D', text: '#501313', label: 'Rechazado', Icon: XCircle },
};

const SectionLabel = ({ icon: Icon, children }: { icon: any; children: React.ReactNode }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <Icon style={{ width: 13, height: 13, color: 'var(--color-text-tertiary)' }} />
        <p style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 }}>
            {children}
        </p>
    </div>
);

const DetailUserLeave = ({ leaveId }: Props) => {
    const { user } = useAuth();
    const [leave, setLeave] = useState<LeaveRequest | null>(null);
    const [loading, setLoading] = useState(true);
    const [usersMap, setUsersMap] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [doc, usersData] = await Promise.all([getLeaveById(leaveId), fetchUsersWithProfiles()]);
                setLeave(doc);
                const map = new Map<string, string>();
                usersData.forEach(u => map.set(u.id, u.name));
                setUsersMap(map);
            } catch (e) {
                console.error(e);
                toast.error('No se pudo cargar la solicitud');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [leaveId]);

    const getName = (id: string) => usersMap.get(id) || 'Usuario desconocido';

    const fDate = (d: string) =>
        new Date(d).toLocaleDateString('es-EC', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });

    const fDateTime = (d: string) =>
        new Date(d).toLocaleDateString('es-EC', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const fCreated = (d: string) =>
        new Date(d).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' +
        new Date(d).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });

    if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    if (!leave) return (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="rounded-full bg-muted p-6"><ClipboardList className="h-10 w-10 text-muted-foreground" /></div>
            <p className="text-lg font-medium">No se encontró la solicitud</p>
        </div>
    );

    const s = STATUS[leave.statusByRrhh as keyof typeof STATUS] || STATUS.pending;

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem><div className="flex items-center gap-1.5"><Files className="h-4 w-4" /><BreadcrumbPage>Solicitudes</BreadcrumbPage></div></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to="/requests">Menu</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests/$id/leaves" params={{ id: user?.$id ?? '' }}>Permisos</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbPage><File className="h-4 w-4" /></BreadcrumbPage></BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="pt-6 w-full pb-16 font-outfit">
                <Card className="shadow-sm overflow-hidden border-0 ring-1 ring-border">

                    <div style={{ height: 3, background: '#EF9F27' }} />

                    {/* Header */}
                    <CardContent className="px-5 sm:px-8 py-6 border-b">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                            <div className="flex items-start gap-4">
                                <div style={{ width: 44, height: 44, borderRadius: 12, background: '#FAEEDA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <ClipboardList style={{ width: 22, height: 22, color: '#854F0B' }} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Solicitud de permiso</p>
                                    <h1 className="text-lg font-medium leading-snug mb-2 wrap-break-word">{leave.title}</h1>
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                                        <div className="flex items-center gap-1.5">
                                            <User className="h-3.5 w-3.5 shrink-0" />
                                            <span>{getName(leave.userId)}</span>
                                        </div>
                                        <span className="hidden sm:inline" style={{ color: 'var(--color-border-secondary)' }}>·</span>
                                        <span className="text-xs">{fCreated(leave.$createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: s.bg, border: `0.5px solid ${s.border}`, borderRadius: 20, padding: '5px 12px', flexShrink: 0, alignSelf: 'flex-start' }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot }} />
                                <s.Icon style={{ width: 13, height: 13, color: s.text }} />
                                <span style={{ fontSize: 12, fontWeight: 500, color: s.text }}>{s.label}</span>
                            </div>
                        </div>
                    </CardContent>

                    {/* Fechas + Duración */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 border-b">
                        <CardContent className="px-5 sm:px-8 py-5 border-b sm:border-b-0 sm:border-r">
                            <SectionLabel icon={CalendarDays}>Fechas</SectionLabel>
                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <span className="text-xs text-muted-foreground w-10 pt-0.5 shrink-0">Inicio</span>
                                    <span className="text-sm font-medium capitalize">{isFullDayLeave(leave) ? fDate(leave.startDate) : fDateTime(leave.startDate)}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-xs text-muted-foreground w-10 pt-0.5 shrink-0">Fin</span>
                                    <span className="text-sm font-medium capitalize">{isFullDayLeave(leave) ? fDate(leave.endDate) : fDateTime(leave.endDate)}</span>
                                </div>
                            </div>
                        </CardContent>
                        <CardContent className="px-5 sm:px-8 py-5">
                            <SectionLabel icon={Timer}>Duración y tipo</SectionLabel>
                            <p className="text-2xl font-medium mb-3">{formatDuration(leave.totalDuration)}</p>
                            <div className="inline-flex items-center gap-1.5 bg-muted rounded-md px-2.5 py-1.5">
                                {isFullDayLeave(leave) ? <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" /> : <Watch className="h-3.5 w-3.5 text-muted-foreground" />}
                                <span className="text-xs text-muted-foreground">{isFullDayLeave(leave) ? 'Día completo' : 'Parcial'}</span>
                            </div>
                        </CardContent>
                    </div>

                    {/* Motivo */}
                    {leave.comment && (
                        <CardContent className="px-5 sm:px-8 py-5 border-b">
                            <SectionLabel icon={ClipboardList}>Motivo de la solicitud</SectionLabel>
                            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{leave.comment}</p>
                        </CardContent>
                    )}

                    {/* Aprobado por */}
                    {leave.approvedById && leave.statusByRrhh === 'approved' && (
                        <CardContent className="px-5 sm:px-8 py-4 border-b">
                            <div className="flex items-center gap-2 text-sm" style={{ color: '#0F6E56' }}>
                                <CheckCircle2 className="h-4 w-4 shrink-0" />
                                <span>Aprobado por: <span className="font-medium">{getName(leave.approvedById)}</span></span>
                            </div>
                        </CardContent>
                    )}

                    {/* Rechazado por */}
                    {leave.idRejectedByRrhh && leave.statusByRrhh === 'rejected' && (
                        <CardContent className="px-5 sm:px-8 py-4 border-b">
                            <div className="flex items-center gap-2 text-sm mb-3" style={{ color: '#A32D2D' }}>
                                <XCircle className="h-4 w-4 shrink-0" />
                                <span>Rechazado por: <span className="font-medium">{getName(leave.idRejectedByRrhh)}</span></span>
                            </div>
                            {leave.rejectionReasonByRrhh && (
                                <div style={{ background: '#FCEBEB', border: '0.5px solid #F09595', borderRadius: 8, padding: '12px 16px' }}>
                                    <p className="text-xs font-medium mb-1" style={{ color: '#791F1F' }}>Motivo del rechazo:</p>
                                    <p className="text-sm" style={{ color: '#A32D2D' }}>{leave.rejectionReasonByRrhh}</p>
                                </div>
                            )}
                        </CardContent>
                    )}

                    {/* Atrás */}
                    <CardContent className="px-5 sm:px-8 py-4">
                        <Button asChild variant="outline" className="w-full h-10">
                            <Link to="/requests/$id/leaves" params={{ id: user?.$id ?? '' }}>
                                <ArrowLeft className="h-4 w-4 mr-2" /> Atrás
                            </Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </>
    );
};

export default DetailUserLeave;